import { tuning } from '@/config/tuning';

/**
 * powerups.ts — the PURE item engine (M12, Mario-Kart style). No three.js, no
 * DOM: just one item slot per racer and the rules for collecting / using /
 * ticking it, so the whole system is deterministic and unit-testable.
 *
 * The loop (same for the player and every AI rival):
 *  1. COLLECT — drive through a floating item box on the course (only with an
 *     empty slot). The box ROLLS a random power, weighted by your race position
 *     (trailing racers get the spicier stuff), and a short roulette spins.
 *  2. HOLD — the rolled item sits in your slot, shown on the HUD button.
 *  3. USE — one button fires it. One item at a time: you can't fire while an
 *     effect is still running, and you can't collect again until the slot is
 *     empty (fired). Magnet and Kick additionally need YOUR ball within
 *     `ballRange` (the race layer enforces that — it owns the positions).
 *
 * The race ticks every racer's slot with `stepPowerups` each fixed step; the
 * car reads `isActive(state, 'boost')` for its boost flag, and the race reads
 * `isActive` for magnet / bulldozer / shield effects exactly as before.
 */

export type PowerupKind =
  | 'boost'
  | 'magnet'
  | 'dozer'
  | 'shield'
  | 'kick'
  | 'tackle'
  | 'wall' // M46: FREE KICK WALL — plant a defensive wall behind you
  | 'offside' // M46: OFFSIDE TRAP — spin out everyone ahead (shield blocks)
  | 'pibe' // M46: PIBE DE ORO — golden star power, last two places only
  | 'studs' // M59: STUDS OUT — spinning wheel spikes, spin rivals + deflect balls (front of pack)
  | 'scorpion' // M59: SCORPION KICK — flip the ball up-and-over untouchable (last two)
  | 'mud'; // M59: SLIPPERY MUD — drop a slowing puddle behind you (top two)

// NOTE: new kinds are APPENDED (the wire codec maps kind→byte by this index),
// so old indices stay put; the SIM_VERSION bump covers the added behaviour.
export const POWERUP_KINDS: readonly PowerupKind[] = [
  'boost',
  'magnet',
  'dozer',
  'shield',
  'kick',
  'tackle',
  'wall',
  'offside',
  'pibe',
  'studs',
  'scorpion',
  'mud',
];

export type PowerupParams = typeof tuning.powerups;
export type ItemWeights = Record<PowerupKind, number>;

/** Most SCORPION KICKs a single racer may receive in ONE match (M60d owner:
 *  "no user should ever receive more than 2 scorpion kicks in one match"). */
export const SCORPION_MAX = 2;

/**
 * M60d (owner): a DEAD-LAST racer who has fallen further behind the pack than a
 * scorpion kick would carry the ball is AUTO-GRANTED a scorpion as their next
 * item — a guaranteed lifeline. Pure decision; the race layer supplies the
 * measured gap + gates.
 *   - isLast     — they are dead last (place === racerCount)
 *   - gapToPack  — arc-length behind the NEAREST racer ahead (edge of the pack)
 *   - reach      — the scorpion's along-course throw (scorpion.autoGrantGap)
 *   - received   — scorpions this racer has already collected this match
 *   - courseOk   — not in the final stretch (the normal scorpion course gate)
 *   - racerCount — a real field (>= 3, same as scorpion eligibility)
 * The cap (received < SCORPION_MAX) is enforced here too, so an auto-grant can
 * never push a racer past two scorpions.
 */
export function autoGrantScorpion(
  isLast: boolean,
  gapToPack: number,
  reach: number,
  received: number,
  courseOk: boolean,
  racerCount: number,
): boolean {
  return (
    isLast && courseOk && racerCount >= 3 && received < SCORPION_MAX && gapToPack > reach
  );
}

/** A racer's single item slot + the currently running effect. */
export interface PowerupState {
  /** The stored item (null = empty slot, free to collect). */
  held: PowerupKind | null;
  /** > 0 while the collect roulette is spinning (item not usable yet). */
  roulette: number;
  /** The effect currently running (null = none). */
  activeKind: PowerupKind | null;
  /** Seconds left on the running effect. */
  activeLeft: number;
}

const DURATION: Record<PowerupKind, keyof PowerupParams> = {
  boost: 'boostDuration',
  magnet: 'magnetDuration',
  dozer: 'dozerDuration',
  shield: 'shieldDuration',
  kick: 'kickDuration',
  tackle: 'tackleDuration',
  wall: 'wallDuration',
  offside: 'offsideDuration',
  pibe: 'pibeDuration',
  studs: 'studsDuration',
  scorpion: 'scorpionDuration',
  mud: 'mudDuration',
};

/** How long this power's effect lasts, from tuning. */
export function durationOf(kind: PowerupKind, p: PowerupParams): number {
  return p[DURATION[kind]] as number;
}

export function createPowerupState(): PowerupState {
  return { held: null, roulette: 0, activeKind: null, activeLeft: 0 };
}

export function resetPowerupState(s: PowerupState): void {
  s.held = null;
  s.roulette = 0;
  s.activeKind = null;
  s.activeLeft = 0;
}

/** Is this power's effect currently running? */
export function isActive(s: PowerupState, kind: PowerupKind): boolean {
  return s.activeKind === kind && s.activeLeft > 0;
}

/** Free to grab a box? (Slot must be empty — fire what you're holding first.) */
export function canCollect(s: PowerupState): boolean {
  return s.held === null;
}

/** Can the held item be fired right now? (Held, roulette done, nothing running.) */
export function canUse(s: PowerupState): boolean {
  return s.held !== null && s.roulette <= 0 && s.activeKind === null;
}

/**
 * Put a rolled item in the slot and start the reveal roulette. No-op (returns
 * false) if the slot is already taken — the race checks `canCollect` first, this
 * is just the belt-and-braces gate.
 */
export function collectItem(s: PowerupState, kind: PowerupKind, p: PowerupParams): boolean {
  if (!canCollect(s)) return false;
  s.held = kind;
  s.roulette = p.rouletteTime;
  return true;
}

/**
 * Fire the held item: empties the slot and starts the effect timer. Returns the
 * kind that fired so the race can apply its physics (kick impulse, dozer
 * transform…), or null if nothing could fire. Range rules (magnet/kick near the
 * ball) live in the race layer, which knows the positions.
 */
export function useItem(s: PowerupState, p: PowerupParams): PowerupKind | null {
  if (!canUse(s)) return null;
  const kind = s.held as PowerupKind;
  s.held = null;
  s.activeKind = kind;
  s.activeLeft = durationOf(kind, p);
  return kind;
}

/**
 * Advance the slot's timers one fixed step. Returns true exactly on the step the
 * collect roulette finishes (so the race can play the "item landed" sound).
 */
export function stepPowerups(s: PowerupState, dt: number): boolean {
  let rouletteDone = false;
  if (s.roulette > 0) {
    s.roulette -= dt;
    if (s.roulette <= 0) {
      s.roulette = 0;
      rouletteDone = true;
    }
  }
  if (s.activeLeft > 0) {
    s.activeLeft -= dt;
    if (s.activeLeft <= 0) {
      s.activeLeft = 0;
      s.activeKind = null;
    }
  }
  return rouletteDone;
}

/** 0..1 remaining fraction of the running effect (for the HUD drain ring). */
export function activeFraction(s: PowerupState, p: PowerupParams): number {
  if (!s.activeKind || s.activeLeft <= 0) return 0;
  const d = durationOf(s.activeKind, p);
  return d > 0 ? s.activeLeft / d : 0;
}

/** Position as a fraction of the field: 0 = leading, 1 = dead last. */
export function placeFraction(place: number, racerCount: number): number {
  if (racerCount <= 1) return 0;
  return (place - 1) / (racerCount - 1);
}

/**
 * Roll a random item, weighted by race position (the Mario-Kart comeback
 * engine, M14 rework): FIRST PLACE rolls defense ONLY (shield/wall — M58,
 * owner rule), the rest of the LEAD tier gets defensive/control items (no raw
 * boosts — they don't need them), the MID tier a balanced spread, and the TAIL
 * tier (bottom third of the field) the spicy comeback stuff — including the
 * SLIDE TACKLE, which only the bottom 33% can ever roll. M46 adds PIBE DE ORO:
 * the rarest item, mixed into the roll ONLY for the LAST TWO places (its
 * weight lives in p.pibe.weight, not the tier tables). Items even the race
 * out and add "anything can happen" chaos without taking skill out of it.
 * `rng01` is one uniform draw in [0,1) — passing it in keeps this pure and lets
 * the race use its seeded Rng (deterministic replays). `farBehind` (M51) marks
 * a roller who is ALSO a significant distance off the back of the pack (the
 * race layer's desperationGap test) — their PIBE odds climb further.
 *
 * M59 adds three position-gated items: STUDS OUT rides the front-of-pack
 * tables directly; SLIPPERY MUD is injected for places 1-2 (top two); SCORPION
 * KICK is injected for the last two places BUT only when `scorpionOk` (the
 * race layer passes false once the firer is inside the final stretch of the
 * course — the item is banned there). Both injections mirror the PIBE gate, so
 * they stay correct as the field resizes.
 */
export function rollItem(
  rng01: number,
  place: number,
  racerCount: number,
  p: PowerupParams,
  farBehind = false,
  scorpionOk = false,
): PowerupKind {
  const frac = placeFraction(place, racerCount);
  let w: ItemWeights = frac < 0.34 ? p.weights.lead : frac >= 0.67 ? p.weights.tail : p.weights.mid;
  // M58 (owner): the RACE LEADER rolls DEFENSE ONLY — shield / free-kick wall
  // (+ M59 STUDS OUT). Never a speed/attack item. (A desperate far-behind
  // roller reaches here with place already forced to last, so this only ever
  // catches the true leader; racerCount 1 is the dev solo path, left on lead.)
  if (place === 1 && racerCount >= 2) w = p.weights.first; // first table carries its own mud (M60)
  // SLIPPERY MUD (M59): the TOP TWO positions can roll it. 1st place's weight
  // lives in the `first` table above (M60 owner tuning); 2nd place gets it
  // INJECTED here so it never leaks to 3rd in a big field (place 2 sits in the
  // lead tier).
  else if (racerCount >= 2 && place === 2) w = { ...w, mud: p.mud.weight };
  // PIBE DE ORO eligibility: the last two places of a real field (never
  // solo). M58b (owner): DEAD LAST rolls it a bit more often than the
  // second-to-last — the golden hand reaches furthest down.
  if (racerCount >= 3 && place >= racerCount - 1) {
    const last = place >= racerCount;
    w = {
      ...w,
      pibe: farBehind
        ? last
          ? p.pibe.weightDesperateLast
          : p.pibe.weightDesperate
        : last
          ? p.pibe.weightLast
          : p.pibe.weight,
    };
    // SCORPION KICK (M59, owner): same last-two eligibility as PIBE, plus the
    // course gate — banned in the final stretch (scorpionOk is false there).
    if (scorpionOk) w = { ...w, scorpion: p.scorpion.weight };
  }
  let total = 0;
  for (const kind of POWERUP_KINDS) total += w[kind];
  let pick = rng01 * total;
  for (const kind of POWERUP_KINDS) {
    pick -= w[kind];
    if (pick < 0) return kind;
  }
  return POWERUP_KINDS[POWERUP_KINDS.length - 1];
}
